import {
  FC,
  ReactNode,
  RefObject,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react'
import { emptyArr } from '../../../constants/misc'
import {
  DraftQuestionnaireEntry,
  EntryKpi
} from '../../../data/model/questionnaire'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import useLoopingConfigData from '../SurveyQuestionCardHeader/useLoopingConfigData'
import KpiRowsDialog from './KpiRowsDialog'
import KpiSettingsDialog from './KpiSettingsDialog'
import {
  KpiSubjectKind,
  getKpiMatrixRows,
  getKpiSubjectKind,
  getTrackedKpiOptions,
  hasTrackingId,
  isMatrixKpiSubject
} from './kpi.utils'
import {
  KpiDialogModel,
  KpiDialogTarget,
  getKpiDialogModel
} from './kpiDialogModel'
import { getKpiRowsDialogModel } from './kpiRowsDialogModel'
import { useKpiMutations } from './useKpiMutations'

interface KpiContextValue {
  kpis: EntryKpi[]
  subjectKind: KpiSubjectKind
  /** False in a looped section or without a Tracking ID: nothing there can become a KPI. */
  canMark: boolean
  /** Whether the question menu and the summary box offer a question-level KPI. */
  canAddQuestionKpi: boolean
  /** Whether the question menu offers marking several matrix rows at once. */
  canMarkRows: boolean
  isSaving: boolean
  openKpiDialog: (target: KpiDialogTarget) => void
  removeKpi: (kpiId: string) => Promise<boolean>
  /** For markers that count towards several KPIs: each has its own Edit in the summary box. */
  focusKpiSummary: () => void
  summaryRef: RefObject<HTMLDivElement>
}

const KpiContext = createContext<KpiContextValue | null>(null)

/** Null outside tracker waves and for entries that can't be KPIs, so callers use `?.`. */
export const useKpi = (): KpiContextValue | null => useContext(KpiContext)

const canAddQuestionKpiTo = (
  entry: DraftQuestionnaireEntry,
  subjectKind: KpiSubjectKind,
  kpiCount: number
) => {
  if (subjectKind === 'slider') {
    // Average is its only number, so a second KPI would repeat the first.
    return kpiCount === 0
  }
  // Matrix KPIs are marked from a row's menu.
  return (
    (subjectKind === 'singleSelect' ||
      subjectKind === 'multiSelect' ||
      subjectKind === 'ranked') &&
    getTrackedKpiOptions(entry).length > 0
  )
}

const canMarkRowsOf = (
  entry: DraftQuestionnaireEntry,
  subjectKind: KpiSubjectKind
) =>
  isMatrixKpiSubject(subjectKind) &&
  getTrackedKpiOptions(entry).length > 0 &&
  getKpiMatrixRows(entry).some(hasTrackingId)

interface Props {
  entry: DraftQuestionnaireEntry
  children: ReactNode
}

export const KpiProvider: FC<Props> = ({ entry, children }) => {
  const waveId = useOptionalWaveId()
  const isLoopingEnabled = !!useLoopingConfigData(entry.sectionId)
    ?.isLoopingEnabled
  const subjectKind = getKpiSubjectKind(entry)
  const kpis = entry.kpis ?? emptyArr
  const [target, setTarget] = useState<KpiDialogTarget | null>(null)
  const [replacing, setReplacing] = useState<{
    target: KpiDialogTarget
    model: KpiDialogModel
  } | null>(null)
  const { setKpi, setRowKpis, removeKpi, replaceKpi, saving } = useKpiMutations(
    entry.id
  )
  const summaryRef = useRef<HTMLDivElement>(null)

  const focusKpiSummary = useCallback(() => {
    summaryRef.current?.scrollIntoView({ block: 'nearest' })
    summaryRef.current?.focus()
  }, [])

  const canMark = !isLoopingEnabled && hasTrackingId(entry)
  const canAddQuestionKpi =
    canMark &&
    !!subjectKind &&
    canAddQuestionKpiTo(entry, subjectKind, kpis.length)
  const canMarkRows =
    canMark && !!subjectKind && canMarkRowsOf(entry, subjectKind)

  const value = useMemo<KpiContextValue | null>(
    () =>
      waveId && subjectKind
        ? {
            kpis,
            subjectKind,
            canMark,
            canAddQuestionKpi,
            canMarkRows,
            isSaving: saving,
            openKpiDialog: setTarget,
            removeKpi,
            focusKpiSummary,
            summaryRef
          }
        : null,
    [
      waveId,
      subjectKind,
      kpis,
      canMark,
      canAddQuestionKpi,
      canMarkRows,
      saving,
      removeKpi,
      focusKpiSummary
    ]
  )

  const replacingModel = replacing?.target === target ? replacing.model : null
  const model =
    value && target ? replacingModel ?? getKpiDialogModel(entry, target) : null
  const rowsModel =
    value && target?.kind === 'rows' ? getKpiRowsDialogModel(entry) : null
  // The dialog can be cancelled mid-save and another one opened.
  const closeIfStill = (savedTarget: KpiDialogTarget | null) =>
    setTarget((current) => (current === savedTarget ? null : current))

  return (
    <KpiContext.Provider value={value}>
      {children}
      {model && (
        <KpiSettingsDialog
          // The draft is seeded once per open, so a new target needs a new dialog.
          key={JSON.stringify(target)}
          entry={entry}
          model={model}
          saving={saving}
          onClose={() => setTarget(null)}
          onSave={async (input, replacedKpi) => {
            const savedTarget = target
            if (replacedKpi && savedTarget) {
              setReplacing({ target: savedTarget, model })
            }
            const saved = replacedKpi
              ? await replaceKpi(replacedKpi, input)
              : await setKpi(input)
            setReplacing((current) =>
              current?.target === savedTarget ? null : current
            )
            if (saved) {
              closeIfStill(savedTarget)
            }
            return saved
          }}
        />
      )}
      {rowsModel && (
        <KpiRowsDialog
          model={rowsModel}
          saving={saving}
          onClose={() => setTarget(null)}
          onSave={async (input) => {
            const savedTarget = target
            const saved = await setRowKpis(
              input,
              (rowId) => rowsModel.rows.find(({ id }) => id === rowId)?.label
            )
            if (saved) {
              closeIfStill(savedTarget)
            }
            return saved
          }}
        />
      )}
    </KpiContext.Provider>
  )
}
